import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { RStatus } from "../../../Application/types";
import {
  type ContentFilterName,
  getEntityTypeFilters,
  getPriorityFilters,
  type ContentFiltersOptions,
  getDateFilters,
} from "../../../AssetResults/config/filters";
import {
  type Filter,
  SortType,
  type FilterItem,
  FilterType,
  type FilterSlice,
  type ContentItem,
} from "features/AssetResults/types";
import { EntityType } from "features/common/types";

export interface Search {
  appliedFilters: FilterSlice;
  sortType: SortType;
}

export interface HistoryResultsState {
  searchInitialized: boolean;
  search: Search;
  filterOptions: ContentFiltersOptions;
  status: RStatus;
  items: ContentItem[];
  errorMessage: string | undefined;
  activePage: number;
  reachedEnd: boolean;
}

export const initialState: HistoryResultsState = {
  search: {
    appliedFilters: {},
    sortType: SortType.Relevant,
  },
  reachedEnd: false,
  filterOptions: {
    date: getDateFilters(),
    entityType: getEntityTypeFilters(getPriorityFilters(true)),
  },
  activePage: 1,
  searchInitialized: false,
  status: RStatus.Idle,
  errorMessage: undefined,
  items: [],
};

const isPrioritiesDisabled = (search: Search | DeepPartial<Search>) => {
  const selected = search?.appliedFilters?.["entityType"];
  return selected === undefined || selected.length !== 1 || selected[0] !== EntityType.Flow;
};

const historySlice = createSlice({
  name: "history",
  initialState: initialState,
  reducers: {
    gotFilters(state, action: PayloadAction<{ key: ContentFilterName; options: Filter }>) {
      state.filterOptions[action.payload.key] = action.payload.options;
    },
    req(state) {
      state.reachedEnd = false;
      state.items = [];
      state.status = RStatus.Pending;
    },
    got(state, action: PayloadAction<{ items: HistoryResultsState["items"] }>) {
      return { ...state, status: RStatus.Got, items: action.payload.items };
    },
    updateStatus(state, action: PayloadAction<{ status: RStatus }>) {
      return {
        ...state,
        status: action.payload.status,
      };
    },
    fetchMore(state) {
      if (state.reachedEnd) return state;
      return {
        ...state,
        status: RStatus.Pending,
        activePage: state.activePage + 1,
      };
    },
    updateSort(state, action: PayloadAction<{ value: SortType }>) {
      state.search.sortType = action.payload.value;
      state.activePage = initialState.activePage;
    },
    resetFilters(state) {
      state.search.appliedFilters = {};
      state.status = RStatus.Pending;
      state.reachedEnd = false;
      state.activePage = initialState.activePage;
    },
    resetFilter(state, action: PayloadAction<Filter>) {
      delete state.search.appliedFilters[action.payload.key];
      state.status = RStatus.Pending;
      state.reachedEnd = false;
      state.activePage = initialState.activePage;
    },
    clearFilters(state) {
      state.search = initialState.search;
      state.status = RStatus.Pending;
      state.reachedEnd = false;
      state.activePage = initialState.activePage;
    },
    updateFilters(state, action: PayloadAction<{ filter: Filter; newValue?: any }>) {
      const { filter, newValue } = action.payload;

      const mapFilters = () => {
        if (filter.type === FilterType.CheckboxArray) {
          const filtered = (filter.items as FilterItem<string>[]).filter((f: FilterItem) =>
            newValue?.includes(f.value),
          );
          return filtered.map((x: FilterItem) => x.value);
        }
        return [];
      };

      const dependentFilters = filter.dependentFilters?.map(x => state.filterOptions[x as ContentFilterName] as Filter);
      dependentFilters?.every(item => item !== undefined) &&
        dependentFilters?.forEach(f => delete state.search.appliedFilters[f.key]);
      if (Array.isArray(newValue)) {
        state.search.appliedFilters[filter.key] = mapFilters();
      } else {
        if (filter.dates?.from || newValue.from) {
          state.search.appliedFilters["from"] = filter.dates?.from ?? newValue.from;
        } else {
          delete state.search.appliedFilters["from"];
        }

        if (filter.dates?.to || newValue.to) {
          state.search.appliedFilters["to"] = filter.dates?.to ?? newValue.to;
        } else {
          delete state.search.appliedFilters["to"];
        }
      }
      state.status = RStatus.Pending;
      state.reachedEnd = false;
      state.activePage = initialState.activePage;
      state.filterOptions.entityType = getEntityTypeFilters(getPriorityFilters(isPrioritiesDisabled(state.search)));
    },
    outOfResults(state) {
      state.reachedEnd = true;
    },
    err(
      state,
      action: PayloadAction<{
        errorMessage: HistoryResultsState["errorMessage"];
      }>,
    ) {
      return {
        ...state,
        status: RStatus.Error,
        errorMessage: action.payload.errorMessage,
      };
    },
    initSearch(state, action: PayloadAction<{ search: Search }>) {
      state.search = { ...state.search, ...action.payload.search }; // NOSONAR
      state.filterOptions.entityType = getEntityTypeFilters(getPriorityFilters(isPrioritiesDisabled(state.search)));
      state.searchInitialized = true;
    },
    reset() {
      return initialState;
    },
  },
});

// actions
export const {
  req,
  got,
  fetchMore,
  clearFilters,
  updateSort,
  updateFilters,
  outOfResults,
  updateStatus,
  err,
  reset,
  initSearch,
  resetFilter,
  resetFilters,
  gotFilters,
} = historySlice.actions;

// reducers
export default historySlice.reducer;
